// The world as a list of things to draw. Pure: `layoutWorld(civ)` turns the
// commonwealth's state into placements on a hex island, so the renderer
// (world.js) only has to diff two lists and animate the difference, and the
// map can be unit-tested without a GPU.
//
// Hexes are axial (q, r), pointy along z to match Kenney's Hexagon Kit
// (tile width 1.0 on x, corners at z = ±0.577). Every placement has a
// stable `id` - that is what lets a house that was there last round stay
// put while a new market pops in.

import { DEFAULT_SITES, sitesOf, wondersOf, goalOf } from '@/engine/cards';
import { MONUMENT_STAGES } from '@/engine/constants';
import {
  DIRS, key, hexDistance, hash, cluster, SITES, siteCentre, baseTerrain, within, allSites,
} from '@/engine/map';

export { hexDistance, cluster, SITES, siteCentre };

import { rivalColour } from '@/engine/rivals';
import { FINDS } from '@/engine/terrain';
import { layoutCity, plotAt, plotCorners } from './cityLayout';
import { roadSegments, roadsOf } from '@/engine/roads';
import { pieceSpots, LANDMARK, SIDE_COLOURS, ROUGH, FLAT_OF, partyHexes, flushY } from './mapPieces';

export const HEX_W = 1.0;
export const HEX_R = HEX_W / Math.sqrt(3); // corner radius, 0.577

export const hexToWorld = (q, r) => ({ x: HEX_W * (q + r / 2), z: 1.5 * HEX_R * r });

// Where a feature stands: the flat top of each terrain model.
export const TILE_TOP = {
  grass: 0.2, dirt: 0.1, sand: 0.2, stone: 0.2, water: 0.1,
  'grass-forest': 0.2, 'grass-hill': 0.2, 'stone-hill': 0.2, 'stone-rocks': 0.2,
  'stone-mountain': 0.2, 'sand-rocks': 0.2, 'water-rocks': 0.1, 'water-island': 0.1,
  'sand-desert': 0.2,
};

// A village stands on FLAT ground: on a mountain or a hill the model was
// drawn half inside the rock (playtest eFmU-, "merged the town into the
// mountain"). The same ground, its relief cleared away.
export const FLAT_UNDER = {
  'stone-mountain': 'stone', 'stone-hill': 'stone', 'stone-rocks': 'stone',
  'grass-hill': 'grass', 'grass-forest': 'grass', 'sand-rocks': 'sand', 'sand-desert': 'sand',
};

// What each region looks like once found.
export const REGION_LOOKS = {
  fertile_valley: { terrain: ['grass', 'grass', 'grass'], features: ['building-farm', 'building-sheep', 'building-watermill'] },
  copper_hills: { terrain: ['stone', 'stone-hill', 'stone-mountain'], features: ['building-mine', null, null] },
  ruins: { terrain: ['stone-rocks', 'stone', 'stone-rocks'], features: [null, 'unit-tower', 'unit-wall-tower'] },
  neighbours: { terrain: ['grass', 'grass-hill', 'grass'], features: ['building-village', null, 'building-tower'] },
  marsh: { terrain: ['water-rocks', 'water-island', 'water-rocks'], features: [null, 'unit-tree', null] },
  old_road: { terrain: ['dirt', 'dirt', 'grass'], features: ['bridge', 'building-cabin', null] },
  great_forest: { terrain: ['grass-forest', 'grass-forest', 'grass-forest'], features: [null, 'dirt-lumber', null] },
  salt_flats: { terrain: ['sand-desert', 'sand', 'sand-desert'], features: [null, 'building-market', null] },
  high_pass: { terrain: ['stone-mountain', 'stone-hill', 'stone-mountain'], features: [null, 'building-tower', null] },
  the_lake: { terrain: ['water-island', 'water', 'water-rocks'], features: ['building-dock', null, null] },
  old_battlefield: { terrain: ['dirt', 'stone-rocks', 'dirt'], features: ['building-wall', null, 'unit-wall-tower'] },
  the_cliffs: { terrain: ['stone-hill', 'stone-rocks', 'stone'], features: ['unit-tower', null, null] },
};

// Kept for callers that only need "where could a region be": the v0.2
// fixed assignment.
export const REGION_SITES = Object.fromEntries(Object.entries(DEFAULT_SITES).map(([d, id]) => [id, { hexes: SITES[d], ...REGION_LOOKS[id] }]));

// Ring-one slots take the houses (two small ones per hex); ring-two slots
// take the civic buildings, markets from one side and libraries from the
// other so the town reads as districts.
const RING1 = DIRS.map((d) => [d[0], d[1]]);
const RING2 = [[2, 0], [2, -1], [2, -2], [1, -2], [0, -2], [-1, -1], [-2, 0], [-2, 1], [-2, 2], [-1, 2], [0, 2], [1, 1]];
export const MARKET_SLOTS = [RING2[0], RING2[11], RING2[1], RING2[10]];
export const LIBRARY_SLOTS = [RING2[6], RING2[5], RING2[7], RING2[4]];
export const FARM_SLOT = RING2[2];
export const FIELD_SLOTS = [RING2[3], RING2[9], RING2[8]];

// Raider camps on the shore, first one north, the second west.

// What a thing on the map looks like.
// From Kenney's Nature Kit (public/models/nature): a raider camp is a tent
// with its flap open, a bandits' den a small closed one, a wolves' den a
// rock. They used to be the hex kit's archery range, cabin and tree, which
// nobody at the table could tell apart (playtest 6).
export const MENACE_MODELS = { camp: 'nature/tent_detailedOpen', den: 'nature/tent_smallClosed', wolves: 'nature/rock_largeA' };
export const OUTPOST_MODEL = 'building-tower';
export const BAND_MODEL = 'banner'; // drawn by world.js, like clouds

/**
 * @param {object} civ the commonwealth as the engine keeps it
 * @returns {{ tiles: Placement[], features: Placement[], clouds: Placement[] }}
 *   Placement = { id, model, q, r, y, rot, dx, dz, scale }
 */
// ---------------------------------------------------------------- the town
// The TV's city is BUILT, not squashed (Matt, 2026-09-25: "it'd be better if
// the buildings didn't live on hexagons, but were independent and lived on
// the triangles within a hexagon... even better... make the city actually
// look like a cohesive grouping of buildings and have it grow"). Kenney's
// Fantasy Town Kit (CC0, public/models/town) is modular on a one-unit grid:
// a wall block is a 1x1x1 cube and a roof sits on top of it. Each recipe is
// a list of [part, x, y, z, rotY] in those units; world.js clones the parts
// into one group and the placement scales the whole thing onto a triangle.
const T = (name) => `town/${name}`;
const C = (name) => `castle/${name}`;
const sails = (x, y, z) => [0, 1, 2, 3].map((k) => [T('blade'), x, y, z, 0, (Math.PI / 2) * k]);
export const TOWN_RECIPES = {
  house: [[T('wall-wood-block'), 0, 0, 0, 0], [T('wall-wood-door'), 0, 0, 0, 0], [T('roof-gable'), 0, 1, 0, 0]],
  // A town that has grown builds up: a stone storey under the timber one.
  house2: [[T('wall-block'), 0, 0, 0, 0], [T('wall-door'), 0, 0, 0, 0], [T('wall-wood-block'), 0, 1, 0, 0], [T('wall-window-shutters'), 0, 1, 0, 0], [T('roof-high-gable'), 0, 2, 0, 0], [T('chimney'), 0, 2.2, 0, 0]],
  field: [[T('hedge'), -0.75, 0, 0, 0], [T('hedge'), -0.25, 0, 0, 0], [T('hedge'), 0.25, 0, 0, 0], [T('fence'), 0, 0, 0, 0], [T('fence'), 0, 0, 0, Math.PI]],
  workshop: [[T('wall-block'), 0, 0, 0, 0], [T('wall-door'), 0, 0, 0, 0], [T('roof-flat'), 0, 1, 0, 0], [T('chimney'), 0, 1, 0, 0]],
  market: [[T('stall-red'), -0.55, 0, 0, 0], [T('stall-green'), 0.55, 0, 0, 0], [T('lantern'), 0, 0, 0.55, 0]],
  granary: [[T('wall-wood-block'), 0, 0, 0, 0], [T('wall-wood-block'), 0, 1, 0, 0], [T('roof-high-point'), 0, 2, 0, 0], [T('cart'), 0.9, 0, 0, 0]],
  warehouse: [[T('wall-block'), -0.5, 0, 0, 0], [T('wall-block'), 0.5, 0, 0, 0], [T('wall-door'), 0.5, 0, 0, 0], [T('roof-gable'), -0.5, 1, 0, 0], [T('roof-gable'), 0.5, 1, 0, 0]],
  mill: [[T('wall-block'), 0, 0, 0, 0], [T('wall-block'), 0, 1, 0, 0], [T('roof-point'), 0, 2, 0, 0], ...sails(0.6, 1.5, 0)],
  library: [[T('wall-block'), 0, 0, 0, 0], [T('wall-window-shutters'), 0, 0, 0, 0], [T('wall-block'), 0, 1, 0, 0], [T('wall-window-shutters'), 0, 1, 0, Math.PI], [T('roof-high-point'), 0, 2, 0, 0], [T('banner-green'), 0, 0, 0, Math.PI / 2]],
  // The Scholars' machines: a squat kiln with a chimney, a still with its
  // cart of barrels, a crucible tower with two chimneys.
  kiln: [[T('wall-block'), 0, 0, 0, 0], [T('roof-point'), 0, 1, 0, 0], [T('chimney'), 0.25, 1, 0, 0]],
  still: [[T('wall-block'), 0, 0, 0, 0], [T('wall-door'), 0, 0, 0, 0], [T('roof-flat'), 0, 1, 0, 0], [T('chimney'), 0, 1, 0, 0], [T('cart'), 0.9, 0, 0, 0]],
  crucible: [[T('wall-block'), 0, 0, 0, 0], [T('wall-block'), 0, 1, 0, 0], [T('roof-high-point'), 0, 2, 0, 0], [T('chimney'), -0.3, 2, 0, 0], [T('chimney'), 0.3, 2, 0, 0]],
  ruin: [[T('wall-broken'), 0, 0, 0, 0], [T('wall-wood-broken'), 0.4, 0, 0.3, 1.2]],
  // The town wall (Kenney Castle Kit): one hex edge is two narrow wall
  // pieces end to end; a tower stands at each corner; one edge is the gate.
  wall: [[C('wall-narrow'), 0.25, 0, -0.5, 0], [C('wall-narrow'), 0.25, 0, 0.5, 0]], // narrow pieces sit on x -0.5..0; +0.25 centres them on the edge
  gate: [[C('wall-narrow'), 0.25, 0, -0.75, 0], [C('wall-narrow-gate'), -0.06, 0, 0.25, 0], [C('flag-banner-short'), 0.3, 0.55, -0.2, 0]],
  tower: [[C('tower-hexagon-base'), 0, 0, 0, 0], [C('tower-hexagon-top'), 0, 1.31, 0, 0], [C('tower-hexagon-roof'), 0, 1.44, 0, 0], [C('flag-pennant'), 0, 2.2, 0, 0]],
  // THE GREAT WALL raises the town's own wall (playtest 11, Matt: its
  // sections "squeeze into the town and then try to do a little castle"):
  // a raised section is the same wall two courses high, its towers a storey taller.
  wall2: [[C('wall-narrow'), 0.25, 0, -0.5, 0], [C('wall-narrow'), 0.25, 0, 0.5, 0], [C('wall-narrow'), 0.25, 1, -0.5, 0], [C('wall-narrow'), 0.25, 1, 0.5, 0]],
  tower2: [[C('tower-hexagon-base'), 0, 0, 0, 0], [C('tower-hexagon-base'), 0, 1.31, 0, 0], [C('tower-hexagon-top'), 0, 2.62, 0, 0], [C('tower-hexagon-roof'), 0, 2.75, 0, 0], [C('flag-pennant'), 0, 3.51, 0, 0]],
  // THE AGES' LOOKS (cards.js AGES). The Founding: a timber palisade (two
  // courses of stakes on each edge) and watch-posts at the corners. The
  // Bronze Age is the stone wall and towers above. The Iron Age builds in
  // stone and raises the towers; the Classical Age a high roof and a banner
  // on every house, and the wall a course higher.
  palisade: [[T('fence'), -0.46, 0, -0.5, 0], [T('fence'), -0.46, 0, 0.5, 0], [T('fence'), -0.46, 0.38, -0.5, 0], [T('fence'), -0.46, 0.38, 0.5, 0]],
  pgate: [[T('fence'), -0.46, 0, -0.75, 0], [T('fence'), -0.46, 0.38, -0.75, 0], [T('banner-green'), -0.4, 0, 0.3, 0]],
  post: [[T('wall-wood-block'), 0, 0, 0, 0], [T('roof-point'), 0, 1, 0, 0]],
  house3: [[T('wall-block'), 0, 0, 0, 0], [T('wall-door'), 0, 0, 0, 0], [T('wall-block'), 0, 1, 0, 0], [T('wall-window-shutters'), 0, 1, 0, 0], [T('roof-high-point'), 0, 2, 0, 0], [T('banner-red'), 0, 1, 0, 0]],
  // The square around the great work: a fountain, two stalls, lanterns.
  fountain: [[T('fountain-round'), 0, 0, 0, 0]],
  stalls: [[T('stall-red'), -0.6, 0, 0, 0], [T('stall-green'), 0.6, 0, 0, 0]],
  lantern: [[T('lantern'), 0, 0, 0, 0]],
  paving: [[T('road'), 0, 0, 0, 0]],
};
// People (KayKit Adventurers, CC0, public/models/kay): one figure per
// strength, in a huddle, each playing its idle loop. Your warbands are
// knights, scouts hooded rogues, pioneers carry a staff; raiders are
// barbarians. `crowd:<figure>:<n>` - world.js builds and animates it.
export const FIGURES = { warband: 'kay/knight', scout: 'kay/rogue_hooded', pioneer: 'kay/mage', raider: 'kay/barbarian' };
// `:s` spreads them over the hex's six spaces: a party alone on its hex (mapPieces.js).
export const crowdModel = (figure, n, spread = false) => `crowd:${FIGURES[figure] || FIGURES.warband}:${Math.max(1, Math.min(5, n || 1))}${spread ? ':s' : ''}`;
export const FIGURE_SCALE = 0.105;
export const TOWN_PARTS = [...new Set(Object.values(TOWN_RECIPES).flat().map((p) => p[0]))];
// Two-plot buildings are drawn a little larger than one-plot ones.
const TOWN_SCALE = { one: 0.2, two: 0.24 };
// Houses fill free triangles; past this many people the new ones go up a storey.
const TALL_AFTER = 6;

/**
 * THE TOWN, drawn once for both screens (playtest 11, Matt: "the capital
 * city takes up three hexes and has a nice little wall around everything;
 * on the Frontier board it only shows stuff built on one hex... they should
 * really match"). Houses on the free triangles, the Stewards' buildings on
 * theirs, the wall with its gate and towers, the roads, the square. `terrainAt`
 * is a Map of hex key -> ground model, for the height things stand at; a hex
 * it does not know is not drawn on (the Frontier's fog).
 */
export const townFeatures = (civ, terrainAt) => {
  const features = [];
  const population = Math.max(0, civ.population || 0);
  // Houses go on the triangles the city's buildings leave free, city hexes
  // first (not the square), then the ring around the square - so the town
  // reads as one close grouping that spreads as it grows.
  const cityPlaced = civ.city ? layoutCity(civ).buildings : [];
  const takenPlots = new Map();
  cityPlaced.forEach((b) => b.plots.forEach((i) => takenPlots.set(`${b.q},${b.r},${i}`, true)));
  const houseHexes = [...(civ.city?.hexes || []), ...RING1]
    .filter(([q, r], i, arr) => !(q === 0 && r === 0) && arr.findIndex(([a, b]) => a === q && b === r) === i);
  const freePlots = [];
  houseHexes.forEach(([q, r]) => {
    for (let i = 0; i < 6; i += 1) if (!takenPlots.has(`${q},${r},${i}`)) freePlots.push([q, r, i]);
  });
  const houseHexKeys = new Set();
  // Since housing (2026-10-15) homes are Houses the Stewards build and they
  // stand among the city's buildings; the one-house-a-person filler only
  // dresses a save from before them.
  const filler = (civ.city?.buildings || []).some((b) => b.type === 'house') ? 0 : population;
  for (let i = 0; i < Math.min(filler, freePlots.length); i += 1) {
    const [q, r, plot] = freePlots[i];
    houseHexKeys.add(key(q, r));
    const { dx, dz } = plotAt(plot);
    const t = terrainAt.get(key(q, r)) || 'grass';
    const facing = Math.atan2(dx, dz); // doors face out of the triangle
    // The age builds the house (cards.js AGES): timber, then stone below
    // timber, then all stone under a high roof.
    const age = civ.age || 0;
    const houseModel = age >= 3 ? 'town:house3' : (age >= 2 || i >= TALL_AFTER ? 'town:house2' : 'town:house');
    features.push(place(`house:${i}`, houseModel, q, r, TILE_TOP[t] ?? 0.2, facing, dx, dz, TOWN_SCALE.one));
  }

  // The Stewards' city: buildings on their hexes, a few per hex, burned
  // ones as rubble. Libraries are Science's and still sit in their ring.
  if (civ.city) {
    // The same triangles the Stewards' phone board uses (cityLayout), so
    // the TV and the phone agree on where every building stands.
    for (const b of cityPlaced) {
      const t = terrainAt.get(key(b.q, b.r)) || 'grass';
      // A House is built of the age (cards.js AGES): timber, stone below
      // timber, all stone under a high roof - the Stewards' Houses too, not
      // only the old filler (playtest 13: the Iron Age promised "the world
      // will look different tomorrow" and the town looked the same).
      const age = civ.age || 0;
      const houseOfAge = age >= 3 ? 'town:house3' : (age >= 2 ? 'town:house2' : 'town:house');
      const recipe = b.burned ? 'town:ruin' : (b.type === 'house' ? houseOfAge : (TOWN_RECIPES[b.type] ? `town:${b.type}` : 'town:workshop'));
      const facing = Math.atan2(b.dx, b.dz);
      features.push(place(`city:${b.id}`, recipe, b.q, b.r, TILE_TOP[t] ?? 0.2, facing, b.dx, b.dz, b.plots.length > 1 ? TOWN_SCALE.two : TOWN_SCALE.one));
    }
  } else {
    for (let i = 0; i < Math.min(civ.markets || 0, MARKET_SLOTS.length); i += 1) {
      const [q, r] = MARKET_SLOTS[i];
      features.push(place(`market:${i}`, 'building-market', q, r, TILE_TOP.grass, hash(q, r, 30) * Math.PI * 2));
    }
  }
  if (!civ.city) {
    for (let i = 0; i < Math.min(civ.libraries || 0, LIBRARY_SLOTS.length); i += 1) {
      const [q, r] = LIBRARY_SLOTS[i];
      features.push(place(`library:${i}`, 'building-wizard-tower', q, r, TILE_TOP.grass, hash(q, r, 31) * Math.PI * 2));
    }
  }

  // 3b. the town: a wall round the city's outer edge, a gate, towers at the
  // corners, paved roads from the square, and the square itself. Matt,
  // 2026-09-25: "I want to make this town look as cool as we possibly can."
  if (civ.city) {
    // The wall goes round everything the town has become: the city's hexes
    // and any hex the houses have spread into.
    const cityHexes = [...civ.city.hexes, ...[...houseHexKeys].map((k) => k.split(',').map(Number))]
      .filter(([q, r], i, arr) => terrainAt.has(key(q, r)) && arr.findIndex(([a, b]) => a === q && b === r) === i);
    const inCity = new Set(cityHexes.map(([q, r]) => key(q, r)));
    const towers = new Map();
    const gateAt = (() => {
      // The gate faces the first village if there is one, else the east.
      const v = (civ.villages || [])[0];
      return v ? Math.atan2(hexToWorld(v.q, v.r).z, hexToWorld(v.q, v.r).x) : 0;
    })();
    let gate = null;
    const edges = [];
    cityHexes.forEach(([q, r]) => {
      const c = hexToWorld(q, r);
      for (let i = 0; i < 6; i += 1) {
        const [, A, B] = plotCorners(i);
        const mx = (A.x + B.x) / 2; const mz = (A.z + B.z) / 2;
        const nx = c.x + mx * 2; const nz = c.z + mz * 2;
        const neighbour = cityHexes.find(([a, b]) => { const w = hexToWorld(a, b); return Math.hypot(w.x - nx, w.z - nz) < 0.2; });
        if (neighbour) continue;
        const along = Math.atan2(B.x - A.x, B.z - A.z);
        const out = Math.atan2(c.z + mz, c.x + mx);
        const d = Math.abs(Math.atan2(Math.sin(out - gateAt), Math.cos(out - gateAt)));
        const corners = [A, B].map((P) => `${(c.x + P.x).toFixed(2)},${(c.z + P.z).toFixed(2)}`);
        const edge = { q, r, i, mx, mz, along, d, out, corners };
        edges.push(edge);
        if (!gate || d < gate.d) gate = edge;
        [A, B].forEach((P, j) => {
          towers.set(corners[j], { q, r, dx: P.x, dz: P.z });
        });
      }
    });
    const WALL_SCALE = 0.29; // two 1-unit pieces = 0.58, one hex edge
    // How many of the Great Wall's three sections stand: the ring, taken
    // round by angle, is split in thirds and a raised third goes up a course.
    const greatWall = wondersOf(civ).includes('walls') ? 3
      : (goalOf(civ) === 'walls' ? Math.min(3, civ.monument?.stages || 0) : 0);
    const byAngle = edges.slice().sort((a, b) => a.out - b.out);
    const raisedEdges = new Set(byAngle.filter((e, n) => Math.floor((n * 3) / byAngle.length) < greatWall));
    const raisedCorners = new Set([...raisedEdges].flatMap((e) => e.corners));
    edges.forEach((e) => {
      const t = terrainAt.get(key(e.q, e.r)) || 'grass';
      // The age's wall: a palisade at the Founding, stone from the Bronze Age,
      // a course higher in the Classical; the Great Wall raises its sections.
      const age = civ.age || 0;
      const model = e === gate ? (age === 0 ? 'town:pgate' : 'town:gate')
        : (raisedEdges.has(e) || age >= 3 ? 'town:wall2' : (age === 0 ? 'town:palisade' : 'town:wall'));
      features.push(place(`townwall:${e.q},${e.r},${e.i}${raisedEdges.has(e) ? ':2' : ''}`, model, e.q, e.r, TILE_TOP[t] ?? 0.2, e.along, e.mx, e.mz, WALL_SCALE));
    });
    for (const [k, tw] of towers) {
      const t = terrainAt.get(key(tw.q, tw.r)) || 'grass';
      const tall = raisedCorners.has(k);
      const age = civ.age || 0;
      const towerModel = age === 0 ? 'town:post' : (tall || age >= 2 ? 'town:tower2' : 'town:tower');
      features.push(place(`towntower:${k}${tall ? ':2' : ''}`, towerModel, tw.q, tw.r, TILE_TOP[t] ?? 0.2, 0, tw.dx, tw.dz, 0.21));
    }
    // Roads: paving stones from the square out to every district and the gate.
    const ends = cityHexes.filter(([q, r]) => q || r).map(([q, r]) => ({ ...hexToWorld(q, r), q, r }));
    if (gate) { const c = hexToWorld(gate.q, gate.r); ends.push({ x: c.x + gate.mx, z: c.z + gate.mz, q: gate.q, r: gate.r }); }
    ends.forEach((e, n) => {
      const len = Math.hypot(e.x, e.z);
      const steps = Math.floor(len / 0.14);
      for (let k = 2; k <= steps; k += 1) {
        const f = k / steps;
        const x = e.x * f; const z = e.z * f;
        // which hex this stone falls in
        const rr = Math.round(z / (1.5 * HEX_R)); const qq = Math.round(x / HEX_W - rr / 2);
        if (!inCity.has(key(qq, rr))) continue;
        const h = hexToWorld(qq, rr);
        const t = terrainAt.get(key(qq, rr)) || 'grass';
        features.push(place(`road:${n}:${k}`, 'town:paving', qq, rr, (TILE_TOP[t] ?? 0.2) + 0.005, Math.atan2(e.x, e.z), x - h.x, z - h.z, 0.12));
      }
    });
    // The square round the great work.
    const t0 = terrainAt.get(key(0, 0)) || 'grass';
    const y0 = TILE_TOP[t0] ?? 0.2;
    const ring = (i) => { const { dx, dz } = plotAt(i); return [dx * 1.05, dz * 1.05]; };
    features.push(place('square:fountain', 'town:fountain', 0, 0, y0, 0, ...ring(3), 0.12));
    features.push(place('square:stalls', 'town:stalls', 0, 0, y0, Math.atan2(...ring(1)), ...ring(1), 0.16));
    [0, 2, 4, 5].forEach((i) => features.push(place(`square:lantern:${i}`, 'town:lantern', 0, 0, y0, 0, ...ring(i), 0.14)));
  }

  return features;
};

export const ROAD_COLOUR = '#b07a3f';
const NEXT = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]];
/**
 * The road as it should be DRAWN, on both screens: every segment between road
 * hexes and the city, plus a spur from a road hex into a village beside it,
 * plus - for a road hex with neither - a stub pointing home, so one hex of
 * road is still a road you can see. Segments are { id, a, b } hex to hex; a
 * stub's `b` is a point half way to its neighbour ({ q, r, half: true }).
 */
export const roadPaths = (civ) => {
  const out = roadSegments(civ).slice();
  const villages = new Set((civ.villages || []).map((v) => key(v.q, v.r)));
  const touched = new Set(out.flatMap((sg) => [key(sg.a.q, sg.a.r), key(sg.b.q, sg.b.r)]));
  for (const k of roadsOf(civ)) {
    const [q, r] = k.split(',').map(Number);
    NEXT.forEach(([dq, dr]) => {
      const n = key(q + dq, r + dr);
      if (!villages.has(n) || roadsOf(civ).includes(n)) return;
      out.push({ id: `${k}>${n}`, a: { q, r }, b: { q: q + dq, r: r + dr } });
      touched.add(k);
    });
    if (touched.has(k)) continue;
    // Alone: point it at the city, the way it will go.
    const [dq, dr] = NEXT.slice().sort((x, y) => hexDistance(q + x[0], r + x[1]) - hexDistance(q + y[0], r + y[1]))[0];
    out.push({ id: `${k}:stub`, a: { q, r }, b: { q: q + dq, r: r + dr }, half: true });
  }
  return out;
};

export const layoutWorld = (civ) => {
  const tiles = [];
  const features = [];
  const clouds = [];
  const terrainAt = new Map();

  const setTile = (q, r, model) => {
    terrainAt.set(key(q, r), model);
  };

  // 1. the island - only what the commonwealth has SEEN (fog for the rest;
  // an old save with no `seen` shows everything)
  const seen = civ.seen ? new Set(civ.seen) : null;
  // Matt, 2026-09-25: "The unexplored territory should just be black...
  // we shouldn't be able to see anything out there." So nothing is drawn
  // that has not been seen - no outer sea, no ring of ice - and the
  // unknown shows only as a dark AURA of hexes at the edge (step 6).
  // The world is generated, not stored (engine/map.js): draw the seen, from
  // the ground function, however far it goes. An old save with no `seen`
  // shows the home island.
  const seed = civ.mapSeed || 0;
  const visible = (q, r) => !seen || seen.has(key(q, r));
  const drawn = seen ? [...seen].map((k) => k.split(',').map(Number)) : within(0, 0, 9);
  drawn.forEach(([q, r]) => setTile(q, r, baseTerrain(q, r, seed)));

  // 2. the sites - unknown (black) until found, then the region's land rises
  // there. They used to be sea under a cloud, which read as ocean.
  const found = new Set(civ.regions || []);
  const sites = sitesOf(civ);
  allSites(civ).forEach((hexes, direction) => {
    const regionId = sites[direction];
    const look = regionId && REGION_LOOKS[regionId];
    hexes.forEach(([q, r], i) => {
      if (!visible(q, r)) { terrainAt.delete(key(q, r)); return; }
      // Seen is seen: an unfound region's ground shows like any other the
      // units can see (playtest 9bkJg); what stands on it waits for the find.
      if (look && (found.has(regionId) || seen)) {
        setTile(q, r, look.terrain[i]);
        const model = found.has(regionId) && look.features[i];
        // A landmark, small at the back of its hex (mapPieces.js LANDMARK).
        // Its height was the ground's NAME here until playtest 12, so the TV
        // never drew one while the phone did - full size, over the units.
        if (model) features.push(place(`region:${regionId}:${i}`, model, q, r, flushY(TILE_TOP[terrainAt.get(key(q, r))] ?? 0.2, model, LANDMARK.scale), hash(q, r, 9) * Math.PI * 2, LANDMARK.dx, LANDMARK.dz, LANDMARK.scale));
      } else {
        terrainAt.delete(key(q, r));
      }
    });
  });

  // 3. the settlement
  const monument = civ.monument || { revealed: false, stages: 0 };
  if (monument.revealed) setTile(0, 0, 'dirt');
  const MONUMENT_MODELS = ['building-wall', 'building-tower', 'building-castle'];
  // A finished wonder STANDS. Completing one resets the monument for the
  // next Vision, and nothing drew the finished one, so the castle vanished
  // the moment it was done (playtest Zt66L). Each stands beside the square.
  const built = wondersOf(civ);
  // The Great Wall is drawn as the town's wall itself (townFeatures), never
  // as a castle beside the square.
  built.filter((id) => id !== 'walls').forEach((id, i) => {
    const a = (i * Math.PI * 2) / 3 + 0.5;
    features.push(place(`wonder:${id}`, 'building-castle', 0, 0, TILE_TOP.sand ?? 0.2, 0, 0.24 * Math.cos(a), 0.24 * Math.sin(a), 0.6));
  });
  if (monument.revealed && monument.stages > 0 && goalOf(civ) !== 'walls') {
    const stage = Math.min(monument.stages, MONUMENT_STAGES) - 1;
    features.push(place(`monument:${stage}`, MONUMENT_MODELS[stage], 0, 0, TILE_TOP.dirt, 0));
  } else if (!monument.revealed && !built.length) {
    features.push(place('monument:trees:0', 'unit-tree', 0, 0, TILE_TOP.grass, 0, 0.15, -0.1));
    features.push(place('monument:trees:1', 'unit-tree', 0, 0, TILE_TOP.grass, 0, -0.2, 0.15));
  }

  features.push(...townFeatures(civ, terrainAt));

  // 3c. roads out in the country (DESIGN.md "ROADS"): paving stones from
  // hex centre to hex centre along every segment whose ends are both seen.
  // Every road hex shows (playtest 12: Brian laid one at Ashby and the TV
  // drew nothing, because a lone road hex has no road neighbour to run to).
  roadPaths(civ).forEach((sg) => {
    if (!terrainAt.has(key(sg.a.q, sg.a.r)) || (!sg.half && !terrainAt.has(key(sg.b.q, sg.b.r)))) return;
    const A = hexToWorld(sg.a.q, sg.a.r); const B = hexToWorld(sg.b.q, sg.b.r);
    const reach = sg.half ? 0.5 : 1;
    const len = Math.hypot(B.x - A.x, B.z - A.z) * reach;
    const n = Math.max(2, Math.round(len / 0.13));
    for (let k = 0; k <= n; k += 1) {
      const f = (k / n) * reach;
      const x = A.x + (B.x - A.x) * f; const z = A.z + (B.z - A.z) * f;
      const [hq, hr] = f <= 0.5 ? [sg.a.q, sg.a.r] : [sg.b.q, sg.b.r];
      const h = hexToWorld(hq, hr);
      const t = terrainAt.get(key(hq, hr)) || 'grass';
      // Wider than the town's paving and the brown of a cart track, so it
      // reads against grass from across the room (playtest 12: "we need to
      // be able to see those roads"). The phone's strips are the same brown.
      features.push({ ...place(`highway:${sg.id}:${k}`, 'town:paving', hq, hr, (TILE_TOP[t] ?? 0.2) + 0.005, Math.atan2(B.x - A.x, B.z - A.z), x - h.x, z - h.z, 0.19), colour: ROAD_COLOUR });
    }
  });

  // 4. the threat, as things on the map: camps and dens, and what holds them
  for (const m of civ.menaces || []) {
    if (!visible(m.q, m.r)) continue;
    const t = terrainAt.get(key(m.q, m.r)) || 'grass';
    // A tent at full size fills the hex; at 0.7 it reads as a camp ON it.
    features.push(place(`menace:${m.id}`, MENACE_MODELS[m.kind] || MENACE_MODELS.camp, m.q, m.r, TILE_TOP[t] ?? 0.2, hash(m.q, m.r, 40) * Math.PI * 2, 0, 0, 0.7));
  }
  // The other peoples on the island: a tower at each seat, a house on every
  // hex they hold. The table shows them as soon as they are in sight.
  for (const rv of civ.rivals || []) {
    (rv.held || []).forEach((k) => {
      const [q, r] = k.split(',').map(Number);
      if (!visible(q, r)) return;
      const seat = q === rv.q && r === rv.r;
      const t = terrainAt.get(key(q, r)) || 'grass';
      features.push({ ...place(`rival:${rv.id}:${seat ? 'seat' : k}`, seat ? 'building-village' : 'unit-house', q, r, flushY(TILE_TOP[t] ?? 0.2, seat ? 'building-village' : 'unit-house', seat ? 0.75 : 0.5), hash(q, r, 61) * Math.PI * 2, 0, 0, seat ? 0.75 : 0.5), colour: rivalColour(rv) });
      // Their land, washed in their colour (after playtest 9: "really unclear
      // on the map what they were"); their banner flies over their home.
      features.push({ ...place(`rival:${rv.id}:claim:${k}`, 'claim', q, r, (TILE_TOP[t] ?? 0.2) + 0.01, 0), colour: rivalColour(rv) });
      if (seat) features.push({ ...place(`rival:${rv.id}:banner`, 'banner', q, r, TILE_TOP[t] ?? 0.2, 0, 0.42, -0.3, 1.6), colour: rivalColour(rv) });
    });
  }
  // What the camps have sent out, walking at something of yours. The table
  // shows them because watching them come across the island is the whole
  // point of the threat being on the map instead of in a counter.
  const spots = pieceSpots(civ);
  for (const x of civ.raiders || []) {
    if (!visible(x.q, x.r)) continue;
    const t = terrainAt.get(key(x.q, x.r)) || 'grass';
    const at = spots.get(`raider:${x.id}`) || { dx: 0, dz: 0, alone: true };
    const theirs = x.from ? rivalColour((civ.rivals || []).find((rv) => rv.id === x.from) || {}) : null;
    features.push({ ...place(`raider:${x.id}`, crowdModel('raider', x.strength, at.alone), x.q, x.r, TILE_TOP[t] ?? 0.2, Math.PI, at.dx, at.dz, FIGURE_SCALE), side: theirs || SIDE_COLOURS.raiders, ...(theirs ? { colour: theirs } : {}) });
  }
  // Finds, once seen - the Frontier's phone drew them and the table did not,
  // so the Frontier could see things nobody else could (playtest fO2WR).
  for (const f of civ.finds || []) {
    if (!visible(f.q, f.r)) continue;
    const what = FINDS[f.kind];
    if (!what) continue;
    const t = terrainAt.get(key(f.q, f.r)) || 'grass';
    features.push(place(`find:${f.id}`, what.model, f.q, f.r, TILE_TOP[t] ?? 0.2, hash(f.q, f.r, 55) * Math.PI * 2, 0, 0, 0.7));
  }
  // The network: what the Frontier has planted, and which of it is a town.
  for (const v of civ.villages || []) {
    if (!visible(v.q, v.r)) continue;
    const t = FLAT_UNDER[terrainAt.get(key(v.q, v.r))] || terrainAt.get(key(v.q, v.r)) || 'grass';
    if (terrainAt.has(key(v.q, v.r))) setTile(v.q, v.r, t);
    // A city is a castle; a town a village; a village an outpost.
    const vm = v.city ? 'building-castle' : (v.chartered ? 'building-village' : OUTPOST_MODEL);
    const vs = v.city ? 0.7 : (v.chartered ? 0.75 : 0.8);
    features.push({ ...place(`village:${v.id}`, vm, v.q, v.r, flushY(TILE_TOP[t] ?? 0.2, vm, vs), 0, 0, 0, vs), ...(v.taken ? { colour: rivalColour(v.taken) } : {}) });
  }
  // The Frontier's forts (the Bronze Age): a tower in the middle of the hex.
  for (const k of civ.forts || []) {
    const [q, r] = k.split(',').map(Number);
    if (!visible(q, r)) continue;
    const t = terrainAt.get(key(q, r)) || 'grass';
    features.push(place(`fort:${k}`, 'unit-wall-tower', q, r, TILE_TOP[t] ?? 0.2, hash(q, r, 31) * Math.PI * 2, 0, 0, 0.8));
  }
  for (const u of civ.units || []) {
    if (!visible(u.q, u.r)) continue;
    const t = terrainAt.get(key(u.q, u.r)) || 'grass';
    // In the MIDDLE of its hex when alone (Matt, playtests 11 and 12), round
    // the edge of the middle when sharing - the same spot the phone uses.
    const at = spots.get(`unit:${u.id}`) || { dx: 0, dz: 0, alone: true };
    features.push({ ...place(`unit:${u.id}`, crowdModel(u.kind || 'warband', u.kind === 'warband' ? u.strength : 1, at.alone), u.q, u.r, TILE_TOP[t] ?? 0.2, 0, at.dx, at.dz, FIGURE_SCALE), side: SIDE_COLOURS.ours });
  }

  // 4b. a party never stands inside a forest or a mountain (mapPieces ROUGH).
  for (const k of partyHexes(civ)) {
    const ground = terrainAt.get(k);
    const flat = FLAT_OF[ground];
    if (!flat) continue;
    const [q, r] = k.split(',').map(Number);
    setTile(q, r, flat);
    // Sunk so its own ground is level with the hex's: only the trees or the peak show.
    features.push(place(`rough:${k}`, ground, q, r, (TILE_TOP[flat] ?? 0.2) - 0.2 * ROUGH.scale, hash(q, r, 9) * Math.PI * 2, ROUGH.dx, ROUGH.dz, ROUGH.scale));
  }

  // 5. (The decorative ships are gone: "that doesn't mean anything... it's
  // confusing." Nothing is drawn that does not mean something.)

  for (const [k, model] of terrainAt) {
    const [q, r] = k.split(',').map(Number);
    tiles.push(place(`tile:${k}`, model, q, r, 0, 0));
  }
  // 6. the aura: one ring of dark hexes around everything drawn - the edge
  // of the known, with nothing visible in it.
  const aura = new Set();
  for (const k of terrainAt.keys()) {
    const [q, r] = k.split(',').map(Number);
    DIRS.forEach(([dq, dr]) => {
      const n = key(q + dq, r + dr);
      if (!terrainAt.has(n)) aura.add(n);
    });
  }
  for (const k of aura) {
    const [q, r] = k.split(',').map(Number);
    tiles.push(place(`aura:${k}`, 'aura', q, r, 0, 0));
  }
  void within;

  return { tiles, features, clouds };
};

const place = (id, model, q, r, y, rot = 0, dx = 0, dz = 0, scale = 1) => ({
  id, model, q, r, y, rot, dx, dz, scale,
});

// Every model the renderer may be asked for, so it can preload the lot.
export const MODELS = [...new Set([
  ...Object.values(MENACE_MODELS), OUTPOST_MODEL, BAND_MODEL, 'aura', 'claim', // banner, aura and claim are drawn, not loaded
  ...Object.values(FINDS).map((f) => f.model),
  ...TOWN_PARTS, ...Object.keys(TOWN_RECIPES).map((k) => `town:${k}`), // recipes are assembled from the parts
  'building-smelter', 'building-house', 'building-cabin', 'building-mill', 'dirt-lumber',
  'grass', 'grass-forest', 'grass-hill', 'dirt', 'sand', 'sand-rocks', 'stone', 'stone-hill',
  'stone-mountain', 'stone-rocks', 'water', 'water-rocks', 'water-island',
  'building-wall', 'building-tower', 'building-castle', 'unit-tree', 'unit-house',
  'building-market', 'building-wizard-tower', 'building-farm', 'building-archery',

  ...Object.values(REGION_LOOKS).flatMap((s) => [...s.terrain, ...s.features.filter(Boolean)]),
])];
